import { Injectable } from '@angular/core';
import { WidgetsContainerAbstract } from 'src/app/abstract/widgets/widgets-container.abstract';
import { WidgetsAbstract } from 'src/app/abstract/widgets/widgets.abstract';
import { WidgetsQuestionModel } from 'src/app/models/widgets/widgets-question.model';
import { WidgetsFieldModel } from 'src/app/models/widgets/widgets-field.model';
import { Logger } from 'src/app/core/logger';
import { camelize } from '@angular-devkit/core/src/utils/strings';
import { ConditionalPrimitive } from 'src/app/models/formly/conditional-primitive.model';
import { WidgetsRadioModel } from 'src/app/models/widgets/widgets-radio.model';
import { WidgetsCheckboxModel } from 'src/app/models/widgets/widgets-checkbox.model';
import { WidgetsFormModel } from 'src/app/models/widgets/widgets-form.model';
import { WidgetsService } from './widgets.service';
import { WidgetsDragAndDropService } from './widgets-drag-drop.service';
import { QuestionEditorService } from './question-editor.service';

@Injectable({
  providedIn: 'root',
})
export class WidgetsNodeService {
  constructor(
    private logger: Logger,
    private widgetsService: WidgetsService,
    private dragAndDropService: WidgetsDragAndDropService,
    private qeService: QuestionEditorService
    ) {
    // Empty
  }

  /**
   * Retrieve Widget Node with specific uuid
   *
   * @deprecated Use getNodeAsync instead
   * @param widgetParent
   * @param uuid of the node to find
   * @returns node found
   */
  public getNode(
    widgetParent: WidgetsAbstract,
    uuid: string
  ): WidgetsAbstract | undefined {
    let foundModel: WidgetsAbstract | undefined;

    // Is this the node I'm looking for?
    if (widgetParent.id === uuid) {
      return widgetParent;
    }

    if (widgetParent instanceof WidgetsContainerAbstract) {
      // This means the current node HAS children
      const model = widgetParent as WidgetsContainerAbstract;
      model.children.forEach((childWidget) => {
        const rc = this.getNode(childWidget, uuid);
        if (rc !== undefined) {
          foundModel = rc;
          return rc;
        }
      });
    }

    return foundModel;
  }

  /**
   * Retrieve Widget Node with specific uuid
   *
   * @param widgetParent
   * @param uuid
   * @returns Promise of found node
   */
  public async getNodeAsync(
    widgetParent: WidgetsAbstract,
    uuid: string
  ): Promise<WidgetsAbstract | undefined> {
    let foundModel: WidgetsAbstract | undefined;

    // Is this the node I'm looking for?
    if (widgetParent.id === uuid) {
      return widgetParent;
    }

    if (widgetParent instanceof WidgetsContainerAbstract) {
      // This means the current node HAS children
      const model = widgetParent as WidgetsContainerAbstract;
      model.children.forEach((childWidget) => {
        const rc = this.getNode(childWidget, uuid);
        if (rc !== undefined) {
          foundModel = rc;
          return rc;
        }
      });
    }

    return foundModel;
  }

  /**
   * Update specified node in the tree.
   * NOTE: If the parent node is the node to be updated it will not work
   *
   * @param widget
   * @param searchedNode
   * @returns Promise of node replaced
   */
  public async updateNodeAsync(
    widget: WidgetsAbstract,
    searchedNode: WidgetsAbstract
  ): Promise<WidgetsAbstract|undefined> {
    const found: WidgetsAbstract | undefined = undefined;
    const root = this.getLayoutRoot();

    // Is this the node I'm looking for?
    if (widget.id === searchedNode.id) {
      // Here we change the node
      this.setLayoutTouched();
      widget.cloneFrom(searchedNode);
      return widget;
    }

    if (widget instanceof WidgetsContainerAbstract) {
      // This means the current node HAS children
      const model = widget as WidgetsContainerAbstract;
      for (const childWidget of model.children) {
        const rc = await this.updateNodeAsync(childWidget, searchedNode);
        if (rc !== undefined) {
          return rc;
        }
      }
    }

    // Didn't found any node with this id - Promise rejected
    // throw new Error("Node not found in tree");
    return found;
  }

  /**
   *
   * @param type Field type (input, checkbox, select)
   * @returns Array with the keys for all fields of a specified type
   */
  public getAllConditionalFields(): ConditionalPrimitive[] {
    const names: Array<ConditionalPrimitive> = [];
    this.getConditionalFields(this.getLayoutRoot(), names, '');
    return names;
  }

  /**
   *
   * @param type Field type (input, checkbox, select)
   * @returns Array with the keys for all fields of a specified type
   */
  public getConditionalFieldsInNode(
    nodeId: string | undefined
  ): ConditionalPrimitive[] {
    const names: Array<ConditionalPrimitive> = [];
    if (nodeId) {
      const startWidget: WidgetsAbstract | undefined = this.getNode(
        this.getLayoutRoot(),
        nodeId
      );
      this.getConditionalFields(startWidget, names, '');
    }
    return names;
  }

  /**
   *
   * @param type Field type (input, checkbox, select)
   * @returns Array with the keys for all fields of a specified type
   */
   public getAllFieldsOfType(type: string): string[] {
    const names: Array<string> = [];
    // If the Form has children, Form Builder is in use
    // If the Form is empty but questionLibrary is not, Question Builder is in use
    const formRoot = this.widgetsService.getLayoutRoot();
    const questionRoot = this.qeService.getQuestionRoot();
    let currentRoot: WidgetsContainerAbstract = formRoot;
    if (currentRoot.children.length === 0 && questionRoot.children.length > 0) {
      currentRoot = questionRoot;
    }
    this.getFieldsOfType(type, currentRoot, names, '');
    return names;
  }

  public getAllFieldsOfLabel(type: string, label: string): string[] {
    const names: Array<string> = [];
    this.getFieldsOfLabel(type, label, this.getLayoutRoot(), names);
    return names;
  }

  /**
   * Checks all fields of the same type and according to the number
   * returns a valid name for the next field of the same type
   *
   * @example
   * For 'radio' as input, 'radio3' is returned,
   * since there are 3 other fields of that type
   * @param type Example: radio
   * @returns Suggested name for the next key. Example: radio3
   */
   public getNextKey(type: string): string {
    const names: string[] = this.getAllFieldsOfType(type);
    if (names.length) {
      // WARNING: Don't use '-' here: it's not a valid JS property name!!!
      // return type + '_' + names.length;
      return type + names.length;
    } else {
      return type;
    }
  }

  /**
   * @example
   * For a field name 'Fire Extinguishers Available' it returns fireExtinguishersAvailable2 for a new key.
   * @param name
   * @returns The name of the widget in lowercase and with '-' instead of spaces + index number
   */
  public getNextKeyForName(name: string): string {
    const names: string[] = this.getAllFieldsOfKey(name);
    // Option 1: initial_question_6
    // let key = name.replace(/ /g, '_');
    // return (key.toLowerCase() + '_' + names.length);
    // Option 2: CamelCase2
    // Just remove special characters
    const newName = name.replace(/[^a-zA-Z ]/g, '');
    const key = camelize(newName);
    return key + names.length;
  }


  /**
   * delete one node from tree
   *
   * @param data WidgetsAbstract to delete
   */
   async deleteNode(data: WidgetsAbstract, parent?: WidgetsAbstract): Promise<boolean> {
    // 1. Get access to the question model (this.data)
    const customWidgetDeleteNode: WidgetsAbstract = data;
    // 2. Retrieve the parent node
    const parentNode = await this.getNodeAsync(parent ? parent : this.getLayoutRoot(), data.parentId ? data.parentId : '');
    if (parentNode) {
      const parentContainer: WidgetsContainerAbstract | undefined = parentNode as WidgetsContainerAbstract;
      const siblings = parentContainer.children.length;

      // 3. iterate through the children of the parent node(parentNode.children)
      // 4. check the childwidget is the right one to delete
      // 5. remove childWigets from the array.
      parentContainer.children = parentContainer.children.filter(
        (childwidget) => childwidget.id !== customWidgetDeleteNode.id
      );

      // Check if we really deleted something
      if (parentContainer.children.length < siblings) {
        // 6. Remove node from global dropListId
        const newDropListIds = this.dragAndDropService.getDroppedIds()
          .filter((deletedId) => deletedId !== customWidgetDeleteNode.id);
        this.dragAndDropService.setDroppedIds(newDropListIds);

        // TODO: 7. Remove node from parent dropListId. Only used to edit a question

        // This triggers the dirty state
        this.setLayoutTouched();
        return true;
      }
    }
    return false;
  }

  public async getSelectedNode(): Promise<WidgetsAbstract | undefined> {
    return this.getSelectedNodeRecursive(this.getLayoutRoot());
  }

  /**
   * Traverse widget tree and make all nodes editable
   * @param widget Root of the node tree to make editable
   */
  public makeEditableNodes(widget: WidgetsAbstract) {
    widget.isEdit = true;
    if (widget instanceof WidgetsContainerAbstract && widget.children) {
      widget.children.forEach((childWidget) => {
        this.makeEditableNodes(childWidget);
      });
    }
  }

  private getLayoutRoot(): WidgetsFormModel {
    return this.widgetsService.getLayoutRoot();
  }

  private setLayoutTouched() {
    this.widgetsService.setLayoutTouched();
  }

  /**
   * Traverse widget tree from one specific node and return array of fields that can have an associated Conditionals
   * that are inside the specified node.
   *
   * @param widget Widget node from where to search for conditional fields
   * @param names Array of Conditional Primitives. When a field that can be a conditional is found, it is added to this array.
   * @param namespace Namespace to prepend to each conditional found
   * @returns void
   */
  private getConditionalFields(
    widget: WidgetsAbstract | undefined,
    names: ConditionalPrimitive[],
    namespace: string
  ) {
    if (widget === undefined) {
      // If no starting node, return without adding names / namespaces
      return;
    }

    if (widget instanceof WidgetsFieldModel) {
      const field: WidgetsFieldModel = widget as WidgetsFieldModel;
      let addField: boolean = false;
      if (field.type === 'input') {
        addField = true;
      } else if (field.type === 'radio') {
        addField = true;
      } else if (field.type === 'checkbox') {
        addField = true;
      }

      if (addField) {
        const conditional = new ConditionalPrimitive();
        conditional.name = field.key;
        conditional.namespace = namespace;
        conditional.type = field.type;
        names.push(conditional);
      }
    } else if (widget instanceof WidgetsQuestionModel) {
      const question: WidgetsQuestionModel = widget as WidgetsQuestionModel;
      // Questions impose their namespace
      // TODO: Check if we want to add section to the namespace (section.question.field)
      // For now, we limit the namespace to the question key
      widget.children.forEach((childWidget) => {
        this.getConditionalFields(childWidget, names, question.key);
      });
    } else if (widget instanceof WidgetsContainerAbstract) {
      // For widgets with children, iterate through the children
      widget.children.forEach((childWidget) => {
        this.getConditionalFields(childWidget, names, namespace);
      });
    }
  }

  private getAllFieldsOfKey(key: string): string[] {
    // Strip ending/trailing numbers from key name
    const strippedKey: string = key.replace(/\d+$/, '');

    const names: Array<string> = [];
    this.getFieldsOfKey(strippedKey, this.getLayoutRoot(), names);
    return names;
  }

  /**
   * Traverse widget tree and return array with all inputs of specified type
   *
   * @param type Field type (input, checkbox, select)
   * @returns Array with the keys for all fields of a specified type
   */
  private getFieldsOfType(
    type: string,
    widget: WidgetsAbstract,
    names: string[],
    namespace: string
  ) {
    if (widget instanceof WidgetsFieldModel ||
      widget instanceof WidgetsRadioModel ||
      widget instanceof WidgetsCheckboxModel) {
      const field: WidgetsFieldModel = widget as WidgetsFieldModel;
      let addField: boolean = false;
      if (type) {
        if (field.type === type) {addField = true;}
      } else {
        addField = true;
      }

      if (addField) {
        if (namespace !== '') {names.push(namespace + '.' + field.key);}
        else {names.push(field.key);}
      }
    } else if (widget instanceof WidgetsQuestionModel) {
      const question: WidgetsQuestionModel = widget as WidgetsQuestionModel;
      // Questions impose their namespace
      // TODO: Check if we want to add section to the namespace (section.question.field)
      // For now, we limit the namespace to the question key
      widget.children.forEach((childWidget) => {
        this.getFieldsOfType(type, childWidget, names, question.key);
      });
    } else if (widget instanceof WidgetsContainerAbstract) {
      // For widgets with children, iterate through the children
      widget.children.forEach((childWidget) => {
        this.getFieldsOfType(type, childWidget, names, namespace);
      });
    }
  }

  /**
   * Traverse widget tree and return array with all inputs of specified type
   *
   * @param type
   * @param label
   * @param widget
   * @param names
   */
  private getFieldsOfLabel(
    type: string,
    label: string,
    widget: WidgetsAbstract,
    names: string[]
  ) {
    if (widget instanceof WidgetsFieldModel) {
      const field: WidgetsFieldModel = widget as WidgetsFieldModel;
      if (field.type) {
        if (field.type === type && field.label === label) {names.push(field.label);}
      } else {
        if (field.label === label) {names.push(field.label);}
      }
    } else if (widget instanceof WidgetsContainerAbstract) {
      widget.children.forEach((childWidget) => {
        this.getFieldsOfLabel(type, label, childWidget, names);
      });
    }
  }

  /**
   *
   * @param key Field key startsWith
   * @returns Array with the keys for all fields of a specified type
   */
  private getFieldsOfKey(
    key: string,
    widget: WidgetsAbstract,
    names: string[]
  ) {
    if (
      widget instanceof WidgetsFieldModel ||
      widget instanceof WidgetsQuestionModel
    ) {
      if (widget.key && widget.key.startsWith(key)) {
        names.push(widget.key);
      }
    }

    // TODO: Traverse widget tree and return array with all widgets that starts with key
    if (widget instanceof WidgetsContainerAbstract && widget.children) {
      widget.children.forEach((childWidget) => {
        this.getFieldsOfKey(key, childWidget, names);
      });
    }
  }

  private getSelectedNodeRecursive(
    widget: WidgetsAbstract
  ): WidgetsAbstract | undefined {
    let foundModel: WidgetsAbstract | undefined;
    if (widget.isSelected) {return widget;}

    if (widget instanceof WidgetsContainerAbstract) {
      // This means the current node HAS children
      const container = widget as WidgetsContainerAbstract;
      container.children.forEach((childWidget) => {
        const rc = this.getSelectedNodeRecursive(childWidget);
        if (rc !== undefined) {
          foundModel = rc;
          return rc;
        }
      });
    }
    return foundModel;
  }
}
